/**
 * MCP OAuth consent + authorization code mint (Gateway PKCE flow)
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/ui/primitives';
import { UnifiedTopbar } from '@shared/ui/layout';
import { mintMcpOAuthCode } from '@/services/mcpOAuthApi';
import { buildOAuthRedirect, isMcpOAuthFlow, parseMcpOAuthSearch } from '@/utils/mcpOAuth';

type FlowState =
  | { status: 'idle' }
  | { status: 'minting' }
  | { status: 'redirecting'; clientCallbackUrl: string; redirectUri: string }
  | { status: 'error'; message: string };

function isCustomProtocolRedirect(uri: string): boolean {
  return /^vscode:/i.test(uri) || /^cursor:/i.test(uri);
}

export function McpOAuthFlow() {
  const [flow, setFlow] = useState<FlowState>({ status: 'idle' });
  const [showManualOpen, setShowManualOpen] = useState(false);

  useEffect(() => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    if (!isMcpOAuthFlow(pathname, search)) return;

    const oauth = parseMcpOAuthSearch(search);
    if (!oauth) {
      setFlow({ status: 'error', message: 'OAuth 파라미터(redirect_uri, code_challenge)가 올바르지 않습니다.' });
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    (async () => {
      setFlow({ status: 'minting' });
      try {
        const res = await mintMcpOAuthCode(oauth);
        if (cancelled) return;
        const clientCallbackUrl = buildOAuthRedirect(oauth.redirectUri, res.code, res.state ?? oauth.state);
        setFlow({
          status: 'redirecting',
          clientCallbackUrl,
          redirectUri: oauth.redirectUri,
        });

        if (isCustomProtocolRedirect(oauth.redirectUri)) {
          window.location.href = clientCallbackUrl;
          timeoutId = window.setTimeout(() => {
            if (!cancelled) setShowManualOpen(true);
          }, 2500);
          return;
        }
        window.location.replace(clientCallbackUrl);
      } catch (err) {
        if (cancelled) return;
        setFlow({
          status: 'error',
          message: err instanceof Error ? err.message : 'MCP OAuth code 발급에 실패했습니다.',
        });
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  const title =
    flow.status === 'error'
      ? 'MCP 연결 실패'
      : flow.status === 'redirecting'
        ? 'MCP 클라이언트로 이동 중'
        : 'MCP 연결 승인';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <UnifiedTopbar isAuthenticated />
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {flow.status === 'error' ? (
                <AlertCircle className="h-5 w-5 text-destructive" />
              ) : (
                <ShieldCheck className="h-5 w-5 text-primary" />
              )}
              {title}
            </CardTitle>
            <CardDescription>
              iTops Portal 계정으로 MCP 클라이언트(Cursor, Claude 등) 연결을 승인합니다.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(flow.status === 'idle' || flow.status === 'minting') && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                authorization code 발급 중...
              </p>
            )}
            {flow.status === 'redirecting' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  IDE로 돌아가는 중입니다. 브라우저에서 &quot;Visual Studio Code 열기&quot;를 묻면 허용하세요.
                </p>
                {(showManualOpen || isCustomProtocolRedirect(flow.redirectUri)) && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      화면이 바뀌지 않으면 아래 버튼을 누르거나 VS Code 창으로 직접 전환한 뒤 알림을 확인하세요.
                    </p>
                    <Button type="button" className="w-full" asChild>
                      <a href={flow.clientCallbackUrl}>VS Code / Cursor로 돌아가기</a>
                    </Button>
                    <p className="text-xs text-muted-foreground break-all">
                      {flow.redirectUri}
                    </p>
                  </>
                )}
              </div>
            )}
            {flow.status === 'error' && (
              <>
                <Alert variant="error" title="오류" description={flow.message} />
                <Button type="button" variant="outline" onClick={() => window.location.assign('/entry')}>
                  Entry로 돌아가기
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
